import { ScrollingModule } from "@angular/cdk/scrolling";
import { ApiService } from "../../services/api.service";
import { MaterialModule } from "../../modules/material/material.module";
import {
  ChangeDetectorRef,
  Component,
  ElementRef, EventEmitter,
  forwardRef,
  HostListener,
  Injector,
  Input,
  Output,

  QueryList, ViewChild,
  ViewChildren
} from "@angular/core";
import { Observable, of, Subject } from "rxjs";
import { catchError, debounceTime, distinctUntilChanged, switchMap } from "rxjs/operators";
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  NgControl
} from "@angular/forms";
import { FilterCondition, TableListRequest } from "../../models/table-list-request.interface";
import { OrderBy } from "../../models/user-list-request.interface";
import { SvgIconComponent } from "../svg-icon/svg-icon.component";
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from "@angular/cdk/overlay";
import { ToasterService } from "../../services/toaster.service";
import { CommonModule } from "@angular/common";
import { CommonUtils } from "../../utils/common-utils";
import { NzEmptyComponent } from "ng-zorro-antd/empty";
import { SpinnerLoaderComponent } from "../spinner-loader/spinner-loader.component";
import { ICellEditorAngularComp } from "ag-grid-angular";
import { ICellEditorParams } from "ag-grid-community";
import { DynamicFormService } from "../../services/dynamic-form.service";
import { IdName } from "../../models/id-name.interface";
import { GridTableService } from "../../services/grid-table.service";

@Component({
  selector: "app-autocomplete-dropdown",
  standalone: true,
  imports: [
    CommonModule,
    ScrollingModule,
    MaterialModule,
    SvgIconComponent,
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    FormsModule,
    NzEmptyComponent,
    SpinnerLoaderComponent
  ],
  templateUrl: "./autocomplete-dropdown.component.html",
  styleUrl: "./autocomplete-dropdown.component.scss",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteDropdownComponent),
      multi: true
    }
  ]
})
export class AutocompleteDropdownComponent implements ControlValueAccessor, ICellEditorAngularComp {
  @Input() apiUrl: string = "";
  @Input() selectFields: string[] = [];
  @Input() filterByKeys: string[] = [];
  @Input() filterByKey: string = ""; //deprecated
  @Input() limit: number = 10;
  @Input() orderBy: OrderBy = {};
  @Input() placeholder: string = "Select";
  @Input() formControl!: FormControl;
  @Input() bindLabel: string = "";
  @Input() id: string = "";
  @Input() bindValue: string = "";
  @Input() skipIds: string[] = [];
  @Input() disable: boolean = false;
  @Input() readonly: boolean = false;
  @Input() additionalFilters: FilterCondition[] = [];
  @Input() displayColumns: Array<{ key: string; header: string }> = [];
  @Input() returnRawValue: boolean = true;
  @Input() allowCustomInput: boolean = false;
  @Input() clientSideSearch: boolean = false;
  @Input() clientSearchData: any[] = [];
  @Input() showIcon: boolean = true;
  @Input() multiSelect: boolean = false;
  @Input() searchUsingExpression: boolean = false; //used for determinants search where search string is required in the expression

  @Output() searchCleared = new EventEmitter<void>();
  @Output() suggestionSelected = new EventEmitter<any>();
  @Output() customInputSelected = new EventEmitter<string>();

  @ViewChild("trigger") trigger!: CdkOverlayOrigin;
  @ViewChildren("optionRow") optionRows!: QueryList<ElementRef>;
  @ViewChild("searchInput") searchInput!: ElementRef<HTMLInputElement>;

  value: any;
  onChange: (value: any) => void = () => {
  };
  onTouched: () => void = () => {
  };

  private params!: any;

  isDisabled: boolean = false;
  searchTerm: string = "";
  previousTerm: string = "";
  focusSearch: boolean = false;
  suggestions: any[] = [];
  showSuggestions = false;
  page = 0;
  canPullMoreData: boolean = false;
  loading = true;
  searchTermSubject: Subject<string> = new Subject<string>();
  selectedItems: Map<string, any> = new Map();
  triggerWidth = 0;
  activeOptionIndex: number = -1;

  searchListRequest: TableListRequest = {
    selectFields: [],
    orderBy: this.orderBy,
    limit: this.limit ?? 10,
    page: this.page ?? 0
  };
  maxVisibleItems: number = this.limit ?? 10;
  overlayPositions: ConnectedPosition[] = [
    {
      originX: "start",
      originY: "bottom",
      overlayX: "start",
      overlayY: "top",
      offsetY: 2      // 8px gap *below* the trigger
    },
    {
      originX: "start",
      originY: "top",
      overlayX: "start",
      overlayY: "bottom",
      offsetY: -2      // 8px gap *above* the trigger
    }
  ];
  ngControl: NgControl | null = null;

  @HostListener("window:resize")
  onResize() {
    this.updateWidth();
  }

  constructor(private apiService: ApiService,
              protected cdr: ChangeDetectorRef,
              private toasterService: ToasterService,
              private injector: Injector,
              private dynamicFormService: DynamicFormService,
              private fb: FormBuilder,
              private gridTableService: GridTableService) {
  }

  ngAfterViewInit() {
    this.updateWidth();
    this.cdr.detectChanges();
  }

  private updateWidth() {
    if (this.trigger?.elementRef?.nativeElement) {
      this.triggerWidth = this.trigger.elementRef.nativeElement.offsetWidth || 0;
    }
  }

  // Set initial value from form control
  writeValue(object: any): void {
    if (this.multiSelect) {
      this.writeValuesForMultiSelect(object ?? []);
    } else {
      this.writeValueForSingleSelect(object);
    }
  }

  reset() {
    this.value = null;
    this.searchTerm = "";
    this.selectedItems.clear();
    this.updateMultiSelectValues();
    this.showSuggestions = false;
    this.validateNow();
  }

  private writeValueForSingleSelect(object: any) {
    if (object && typeof object === "object") {
      if (object.hasOwnProperty("key")) {
        this.value = object?.key ?? "";
        this.searchTerm = object.value || this.value;
      } else if (object.hasOwnProperty("id")) {
        this.value = object?.id ?? "";
        this.searchTerm = object?.name ?? "";
      } else {
        this.value = object[this.bindValue] ?? "";
        this.searchTerm = CommonUtils.getNestedValue(object, this.bindLabel) || this.value;
      }
    } else if (object && typeof object === "string") {
      this.value = object;
      this.searchTerm = object;
    } else {
      this.value = "";
      this.searchTerm = "";
    }
    if (!this.returnRawValue) {
      this.onChange(this.value);
    }
  }

// writeValue(value: any): void {
  //   if (value) {
  //     if (typeof value === 'string') {
  //       this.value.key = '';
  //       this.value.label = value;
  //     } else if (typeof value === 'object' && value !== null) {
  //       this.value = value
  //     }
  //   } else {
  //     this.value.key = '';
  //     this.value.label = '';
  //   }
  // }

  // get searchTerm() {
  //   return this.value.label;
  // }

  // When value changes in the form control
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  // On blur or touch event
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  // If the form control is disabled
  setDisabledState(isDisabled: boolean): void {
    this.disable = isDisabled;
  }

  fetchSuggestionsV2() {

  }

  fetchSuggestions() {
    if (this.clientSideSearch) {
      this.suggestions = this.clientSearchData.filter((item: any) =>
        item[this.filterByKeys[0] ?? "name"].toLowerCase().includes(this.searchTerm.toLowerCase())
      );
      this.showSuggestions = true;
      this.activeOptionIndex = this.suggestions.length > 0 ? 0 : -1;

      // For AG Grid, ensure overlay is properly displayed
      this.forceOverlayDisplay();
    } else {
      this.loading = true;
      const payload = this.getRequestPayload();
      this.apiService.getSuggestions(this.apiUrl, payload).subscribe({
        next: (res) => {
          const newRecords = res.data.records || [];
          if (this.page === 0) {
            this.suggestions = newRecords; // Initial load
            this.activeOptionIndex = this.suggestions.length > 0 ? 0 : -1;
          } else {
            this.suggestions = [...this.suggestions, ...newRecords]; // Append to existing data
            this.activeOptionIndex = 0;
          }
          this.setSelectedItems();

          if (newRecords.length < this.limit || newRecords.length === 0) {
            this.canPullMoreData = false;
          } else {
            this.canPullMoreData = true;
          }
          this.loading = false;
          this.showSuggestions = true;

          // For AG Grid, ensure overlay is properly displayed
          this.forceOverlayDisplay();
        },
        error: (err) => {
          this.toasterService.error(err.error.errorDesc);
          console.error("Error fetching suggestions:", err);
          this.loading = false;
        }
      });
    }

  }

  private setSelectedItems() {
    this.selectedItems.forEach((item: any, key: string) => {
      const record = this.suggestions.find(suggestion => suggestion[this.bindValue] == key);
      if (record) {
        record.selected = true;
        this.selectedItems.set(key, record);
      }
    });
    this.updateMultiSelectValues();
  }

  getRequestPayload(): TableListRequest {
    // determinants search where search string is required in the expression
    if (this.searchUsingExpression) {
      return {
        orderBy: this.orderBy,
        limit: this.limit,
        page: this.page,
        where: {
          filterConditions: [],
          expression: this.searchTerm
        }
      };
    }

    const payload: TableListRequest = {
      selectFields: this.selectFields,
      orderBy: this.orderBy,
      limit: this.limit,
      page: this.page
    };

    const filterConditions: FilterCondition[] = this.filterByKeys.map((field) => ({
      name: field,
      alias: field,
      operator: "CONTAINS",
      value: [this.searchTerm]
    }));

    console.log(filterConditions);

    let expression = filterConditions
      .map(fc => fc.alias)
      .join(" OR ");

    if (this.skipIds.length > 0) {
      filterConditions.push({
        name: "id",
        alias: "skipCondition",
        operator: "NOT_IN",
        value: this.skipIds
      });
      expression = `(${expression}) AND skipCondition`;
    }
    console.log("this.additionalFilters: ", this.additionalFilters);
    if (this.additionalFilters && this.additionalFilters.length > 0) {
      const filterExpressions: string[] = [];
      this.additionalFilters.forEach((filter: any) => {
        filterConditions.push(filter);
        filterExpressions.push(filter.alias);
      });
      expression = expression ? `(${expression}) AND (${filterExpressions.join(" AND ")})` : `(${filterExpressions.join(" AND ")})`;
    }

    payload.where = {
      filterConditions: filterConditions,
      expression: expression
    };
    return payload;
  }


  ngOnInit() {
    // this.writeValue(this.preSelectedValue);
    // this.preSelectedValue = {};
    this.ngControl = this.injector.get(NgControl, null);
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }

    // filter out skipIds list if it has null, undefined and empty string
    this.skipIds = this.skipIds.filter(
      (item) => item !== null && item !== undefined && item !== ""
    );
    this.searchTermSubject
      .pipe(
        debounceTime(300), // Wait for 500ms pause in typing
        distinctUntilChanged(), // Only continue if the search term changed
        switchMap(() => {
          if (this.searchTerm === "") return of([]); //prevents multiple API calls when searchTerm is cleared
          if (this.clientSideSearch) {
            // Handle client-side filtering
            this.loading = false;
            return of(
              this.clientSearchData.filter((item: any) =>
                item[this.filterByKeys[0] ?? "name"].toLowerCase().includes(this.searchTerm.toLowerCase())
              )
            );
          } else {
            // Cancel previous API call and start a new one
            this.loading = true;
            return this.apiService
              .getSuggestions(this.apiUrl, this.getRequestPayload())
              .pipe(
                catchError((err) => {
                  console.error("Error fetching suggestions:", err);
                  this.loading = false;
                  return of([]); // Return an empty array if an error occurs
                })
              );
          }
        })
      )
      .subscribe((res: any) => {
        let newRecords: any[] = [];
        if (this.clientSideSearch) {
          newRecords = res;
        } else if (res && res.data && Array.isArray(res.data.records)) {
          newRecords = res.data.records;
        } else {
          return;
        }

        if (this.page === 0) {
          this.suggestions = newRecords; // Initial load
        } else {
          this.suggestions = [...this.suggestions, ...newRecords]; // Append to existing data
        }
        this.setSelectedItems();
        this.forceOverlayDisplay()

        //this.canPullMoreData = newRecords.length >= this.limit;
        this.canPullMoreData = !this.clientSideSearch ? newRecords.length >= this.limit : false;
        this.loading = false;
        this.showSuggestions = true;
        this.activeOptionIndex = this.suggestions.length > 0 ? 0 : -1;
      });
  }

  // Handle lazy loading / pagination on scroll
  onScroll(event: any) {
    const element = event.target;
    const threshold = 100; // pixels from bottom to trigger load more

    // Calculate if we're near the bottom
    const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < threshold;

    // Only fetch more if we're near bottom and can pull more data
    if (isNearBottom && this.canPullMoreData && !this.loading) {
      this.page++;
      this.fetchSuggestions();
    }
  }

  // calculateViewportHeight(resultCount: number): number {
  //   const itemHeight = 50; // Height of each result item in pixels
  //   const visibleItems = Math.min(resultCount, this.maxVisibleItems); // Calculate the number of visible items
  //   return visibleItems * itemHeight; // Set the viewport height dynamically
  // }

  // onScroll(): void {
  //   // Check if we can load more data and the loading is not already in progress
  //   if (this.canPullMoreData && !this.loading) {
  //     this.page++; // Increase the page number
  //     this.fetchSuggestions(); // Fetch more suggestions
  //   }
  // }

  updateSearchTerm() {
    const searchTerm = this.searchTerm;
    this.page = 0; // Always reset page for new search
    this.activeOptionIndex = -1;
    if (!this.searchTerm.trim()) {
      this.value = null;
      this.onChange(null);
      this.searchCleared.emit();
      this.showSuggestions = false;
    } else {
      this.searchTermSubject.next(searchTerm);
    }
  }

  handleEnter(event: KeyboardEvent) {
    // if (this.searchTerm === "%%%") {
    //   this.searchTerm = ""; // reset searchTerm
    //   this.page = 0;
    //   this.fetchSuggestions();
    // }
  }

  select(result: any) {
    if (!this.multiSelect) {
      this.searchTerm = CommonUtils.getNestedValue(result, this.bindLabel).trim() || "";
      this.handleSelectionChange(result);
    } else {
      this.handleSelectionChangeMultiSelect(result);
    }
    this.validateNow();
    this.gridTableService.commitAndRefocusCell(this.params);
  }

  selectCustomInput() {
    const value = this.searchTerm.trim();
    this.value = value;
    this.onChange(this.value);
    this.onTouched();
    this.showSuggestions = false;
    this.customInputSelected.emit(this.searchTerm);
  }

  // Handle the selection change in the autocomplete
  handleSelectionChange(selectedValue: any): void {
    this.value = selectedValue[this.bindValue];
    this.onChange(this.returnRawValue ? { key: this.value, value: this.searchTerm } : this.value);
    this.onTouched();
    this.showSuggestions = false;
    this.suggestionSelected.emit(selectedValue);
  }

  onInputFocus() {
    this.focusSearch = true;
    this.activeOptionIndex = this.suggestions.length > 0 ? 0 : -1;
    // if(this.value) {
    //   this.page = 0;
    //   this.fetchSuggestions();
    // }
    // if (this.suggestions.length > 0) {
    //   this.suggestions = [];
    //   this.updateSearchTerm();
    // }
  }

  onInputFocusOut() {
    this.focusSearch = false;
    console.log(this.value);
    console.log(this.searchTerm);
    if ((!this.allowCustomInput && !this.value) || !this.searchTerm.trim() || this.multiSelect) {
      this.onTouched();
      this.searchTerm = "";
    }
  }

  onInputKeydown(event: KeyboardEvent) {
    if (!this.showSuggestions || this.suggestions.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (this.activeOptionIndex < this.suggestions.length - 1) {
        this.activeOptionIndex++;
        this.scrollToActiveOption();
      } else if (this.canPullMoreData && !this.loading) {
        this.page++;
        this.fetchSuggestions();
      }
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (this.activeOptionIndex > 0) {
        this.activeOptionIndex--;
      } else {
        this.activeOptionIndex = this.suggestions.length - 1;
      }
      this.scrollToActiveOption();
    } else if (event.key === "Enter") {
      if (this.activeOptionIndex >= 0 && this.activeOptionIndex < this.suggestions.length) {
        this.select(this.suggestions[this.activeOptionIndex]);
      }
    } else if (event.key === "Escape") {
      this.showSuggestions = false;
    }
  }

  onInputPaste(event: ClipboardEvent) {
    setTimeout(() => {
      this.updateSearchTerm();
    }, 0);
  }

  scrollToActiveOption() {
    setTimeout(() => {
      if (this.optionRows && this.activeOptionIndex >= 0) {
        const option = this.optionRows.toArray()[this.activeOptionIndex];
        if (option && option.nativeElement) {
          option.nativeElement.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
      }
    }, 0);
  }

  handleSelectionChangeMultiSelect(selectedValue: any): void {
    if (this.selectedItems.has(selectedValue[this.bindValue])) {
      this.selectedItems.delete(selectedValue[this.bindValue]);
    } else {
      this.selectedItems.set(selectedValue[this.bindValue], selectedValue);
    }

    this.suggestions.forEach(suggestion => {
      suggestion.selected = this.selectedItems.has(suggestion[this.bindValue]);
    });

    this.updateMultiSelectValues();
    this.suggestionSelected.emit(Array.from(this.selectedItems.values()));
  }

  private updateMultiSelectValues() {
    this.value = Array.from(this.selectedItems.values());
    this.onChange(
      this.value.map((v: any) => (
        this.returnRawValue ? {
          key: v[this.bindValue],
          value: CommonUtils.getNestedValue(v, this.bindLabel)
        } : v[this.bindValue]
      ))
    );
    this.onTouched();
  }

  removeAllSelections() {
    this.selectedItems.clear();
    this.showSuggestions = false;
    this.updateMultiSelectValues();
    this.validateNow();
    this.suggestionSelected.emit([]);
  }

  private writeValuesForMultiSelect(objects: any[]) {
    this.selectedItems.clear();
    if (objects.length === 0) {
      this.updateMultiSelectValues();
      return;
    }

    for (const obj of objects) {
      if (obj && typeof obj === "object" && obj.hasOwnProperty("key")) {
        this.selectedItems.set(obj.key, {
          [this.bindValue]: obj.key,
          [this.bindLabel]: CommonUtils.getNestedValue(obj, this.bindLabel)
        });
      } else {
        this.selectedItems.set(obj, null);
      }
    }
    this.updateMultiSelectValues();
  }

  hasError() {
    return this.ngControl?.control?.invalid && this.ngControl.control?.touched && this.ngControl.control?.errors;
  }

  protected readonly CommonUtils = CommonUtils;

  setActiveOption(index: number) {
    this.activeOptionIndex = index;
  }

  private focusInSearchBar() {
    setTimeout(() => {
      this.updateWidth();
      this.searchInput.nativeElement.focus();
      this.searchInput.nativeElement.select();
    }, 0);
  }

  handleInput() {
    if (this.searchTerm === "??") {
      this.searchTerm = ""; // reset searchTerm
      this.page = 0;
      this.fetchSuggestions();
      return;
    }
    this.validateNow();
  }

  private validateNow() {
    this.params?.validate?.();
  }


  //AG Grid Related Methods
  agInit(params: ICellEditorParams): void {
    this.params = params;
    Object.keys(params).forEach((k: string) => {
      if ((this as any).hasOwnProperty(k)) {
        (this as any)[k] = (params as any)[k];
      }
    });


    console.log("Inside agInit params: ", params);
    this.writeValue(params.value);
  }

  getValue(): any {
    return { [this.bindValue]: this.value, [this.bindLabel]: this.searchTerm };
  }

  getValidationElement(): HTMLElement {
    return this.searchInput.nativeElement;
  }

  isCancelAfterEnd(): boolean {
    return false;
  }

  afterGuiAttached() {
    this.focusInSearchBar();
  }

  private forceOverlayDisplay() {
    if(!this.params) return;
    this.cdr.detectChanges();

    // Additional timeout to ensure overlay is properly positioned
    setTimeout(() => {
      this.cdr.detectChanges();
    }, 100);
  }

  getValidationErrors(): string[] | null {
    return this.gridTableService.validateEditableGrid(this.params.col, this.value);
  }
}
